export function flattenFlow(flowData) {
  const { flowJson, roleAssignments } = flowData;
  const { blocks, lvl1_connections } = flowJson || {};

  const finalTasks = [];
  const finalConnections = [];

  // Hilfsstrukturen
  const tasksById = new Map();
  const groupChildren = new Map();           // groupId => [childId, ...]
  const groupChildConnections = new Map();   // groupId => [{ from, to }, ...]
  const taskGroupNames = new Map();          // taskId => Gruppenname
  const groupNameToStartTasks = new Map();     // Gruppenname => [childId, ...] (Start-Tasks)
  const groupNameToEndTasks = new Map();       // Gruppenname => [childId, ...] (End-Tasks)

  // Hilfsfunktionen für Gruppenverbindungen
  function getGroupTasksWithNoIncoming(groupId) {
    const cConnections = groupChildConnections.get(groupId) || [];
    const allChildren = groupChildren.get(groupId) || [];
    const toSet = new Set(cConnections.map(c => c.to));
    return allChildren.filter(childId => !toSet.has(childId));
  }
  function getGroupTasksWithNoOutgoing(groupId) {
    const cConnections = groupChildConnections.get(groupId) || [];
    const allChildren = groupChildren.get(groupId) || [];
    return allChildren.filter(childId => {
      return !cConnections.some(conn => conn.from === childId);
    });
  }

  // 1. Blocks durchgehen und Aufgaben sowie Gruppen erfassen
  blocks.forEach(block => {
    if (block.type === "task") {
      // Top-Level Task
      tasksById.set(block.instance_id, block);
    } else if (block.type === "group") {
      // Gruppe: Kinder extrahieren
      const { children_blocks = [], children_connections = [], name: gruppenName } = block;
      const childIds = children_blocks.map(child => child.instance_id);
      groupChildren.set(block.instance_id, childIds);
      groupChildConnections.set(
        block.instance_id,
        children_connections.map(conn => ({
          from: conn.from,
          to: conn.to
        }))
      );
      // Die Kinder-Tasks werden in tasksById aufgenommen und erhalten den Gruppennamen
      children_blocks.forEach(childTask => {
        tasksById.set(childTask.instance_id, childTask);
        taskGroupNames.set(childTask.instance_id, gruppenName);
      });
      // Für die Gruppe: Berechne Start- und End-Tasks und speichere sie anhand des Gruppennamens
      const startTasks = getGroupTasksWithNoIncoming(block.instance_id);
      groupNameToStartTasks.set(gruppenName, startTasks);
      const endTasks = getGroupTasksWithNoOutgoing(block.instance_id);
      groupNameToEndTasks.set(gruppenName, endTasks);
    }
  });

  // 2. Tasks konvertieren – whiteboard-spezifische Felder entfernen, taskAssignees und (falls vorhanden) gruppenname hinzufügen
  for (const [instanceId, block] of tasksById.entries()) {
    const { name, clickup_vorlage_id, rolle, duration_days } = block;
    const newTask = {
      id: instanceId,
      name,
      clickup_vorlage_id,
      duration_days,
      taskAssignees: rolle && roleAssignments[rolle] ? [roleAssignments[rolle]] : []
    };
    if (taskGroupNames.has(instanceId)) {
      newTask.gruppenname = taskGroupNames.get(instanceId);
    }
    finalTasks.push(newTask);
  }

  // 3. Zuerst: Verbindungen innerhalb von Gruppen direkt übernehmen
  for (const [groupId, connections] of groupChildConnections.entries()) {
    connections.forEach(conn => {
      finalConnections.push({ from: conn.from, to: conn.to });
    });
  }

  // 4. Top-Level Verbindungen (lvl1_connections) verarbeiten
  lvl1_connections.forEach(conn => {
    let { from, to } = conn;
    let fromIds = [];
    let toIds = [];

    // Ersetze "from": Falls die Quelle in einer Gruppe liegt, nutze deren End-Tasks
    if (groupChildren.has(from)) {
      // Hier wurde ein Gruppenblock als Quelle angegeben
      fromIds = getGroupTasksWithNoOutgoing(from);
    } else if (taskGroupNames.has(from)) {
      // Falls der Task Teil einer Gruppe ist, ersetzen wir ihn durch den End-Task der Gruppe
      const groupName = taskGroupNames.get(from);
      fromIds = groupNameToEndTasks.get(groupName) || [from];
    } else {
      fromIds = [from];
    }

    // Ersetze "to": Falls das Ziel in einer Gruppe liegt, nutze deren Start-Tasks
    if (groupChildren.has(to)) {
      toIds = getGroupTasksWithNoIncoming(to);
    } else if (taskGroupNames.has(to)) {
      const groupName = taskGroupNames.get(to);
      toIds = groupNameToStartTasks.get(groupName) || [to];
    } else {
      toIds = [to];
    }

    // Füge alle Kombinationen hinzu
    fromIds.forEach(fId => {
      toIds.forEach(tId => {
        finalConnections.push({ from: fId, to: tId });
      });
    });
  });

  return {
    Tasks: finalTasks,
    Connections: finalConnections
  };
}


// ---------------------------------------------------
// Beispiel-Aufruf mit deinem JSON aus der Frage:
const originalData = {
  "startDate": "2025-02-08",
  "unlockedStatus": "🚀 kampagnenstart",
  "roleAssignments": {
    "grafiker": "42454623"
  },
  "flowJson": {
    "id": "recLRzczfoIqVfc9v",
    "name": "TesfFlow2",
    "lvl1_connections": [
      {
        "from": "inst_1740162666408", // Das ist die Group
        "to": "inst_1740156683946"
      }
    ],
    "blocks": [
      {
        "type": "task",
        "instance_id": "inst_1740156683946",
        "template_id": "recCPr1cBq98LKzLP",
        "instance_pos": { "x": 1141, "y": 538 },
        "name": "Task A",
        "clickup_vorlage_id": "tmp_1c32b",
        "rolle": "grafiker",
        "duration_days": 3
      },
      {
        "type": "group",
        "instance_id": "inst_1740162666408",
        "template_id": "reca2X6fLYeYu2bUA",
        "instance_pos": { "x": 485, "y": 427 },
        "name": "new Group - Test2",
        "children_connections": [
          {
            "from": "inst_1740094801688",
            "to": "inst_1740127964694"
          }
        ],
        "children_blocks": [
          {
            "type": "task",
            "instance_id": "inst_1740094801688",
            "template_id": "recCPr1cBq98LKzLP",
            "instance_pos": { "x": -136.34, "y": 124.97 },
            "name": "Task A",
            "clickup_vorlage_id": "tmp_1c32b",
            "rolle": "grafiker",
            "duration_days": 3
          },
          {
            "type": "task",
            "instance_id": "inst_1740127964694",
            "template_id": "recCPr1cBq98LKzLP",
            "instance_pos": { "x": 248.32, "y": 125.48 },
            "name": "Task A",
            "clickup_vorlage_id": "tmp_1c32b",
            "rolle": "grafiker",
            "duration_days": 3
          }
        ]
      }
    ],
    "roles": ["grafiker"]
  },
  "namingConvention": {
    "groupTaskInTask": true,
    "workflowNameInTask": true
  }
};


export function calculateDueDates(flattened, startDateStr) {
  const tasks = flattened.Tasks;
  const connections = flattened.Connections;

  // Map: Task-ID → Task (zur schnellen Referenz)
  const taskMap = new Map();
  tasks.forEach(task => {
    taskMap.set(task.id, { ...task });
  });

  // Erstelle eine Map, die für jeden Task alle direkten Vorgänger (Predecessors) speichert.
  const prerequisites = {};
  tasks.forEach(task => {
    prerequisites[task.id] = [];
  });
  connections.forEach(conn => {
    // Für jede Verbindung wird der "from"-Task als Vorgänger des "to"-Tasks betrachtet.
    if (prerequisites[conn.to]) {
      prerequisites[conn.to].push(conn.from);
    } else {
      prerequisites[conn.to] = [conn.from];
    }
  });

  // Hilfsfunktionen
  function parseDate(str) {
    // Erwartetes Format: "DD.MM.YYYY"
    const [day, month, year] = str.split('.').map(Number);
    return new Date(year, month - 1, day);
  }

  function formatDate(date) {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  }

  function addDays(date, days) {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  }

  function adjustForWeekend(date) {
    // Wenn das Datum auf einen Samstag (6) oder Sonntag (0) fällt, verschiebe auf Montag
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 6) {       // Samstag
      return addDays(date, 2);
    } else if (dayOfWeek === 0) { // Sonntag
      return addDays(date, 1);
    }
    return date;
  }

  // Wir speichern in dueDates für jeden Task das errechnete { start, deadline } als Date-Objekte.
  const dueDates = {};

  // Rekursive Funktion: Berechnet für einen Task die früheste Startzeit und die Deadline.
  function computeDueDates(taskId) {
    // Falls bereits berechnet, gib das Ergebnis zurück
    if (dueDates[taskId]) return dueDates[taskId];

    let earliestStart;
    // Hat der Task keine Vorgänger? Dann ist sein Startdatum das globale Startdatum.
    if (!prerequisites[taskId] || prerequisites[taskId].length === 0) {
      earliestStart = parseDate(startDateStr);
    } else {
      // Andernfalls: earliestStart = Maximum der Deadlines aller Vorgänger
      let maxDate = null;
      prerequisites[taskId].forEach(predId => {
        const predDates = computeDueDates(predId);
        if (!maxDate || predDates.deadline > maxDate) {
          maxDate = predDates.deadline;
        }
      });
      earliestStart = maxDate;
    }

    // Berechne die Deadline: start + duration_days
    const task = taskMap.get(taskId);
    let tentativeDeadline = addDays(earliestStart, task.duration_days);
    tentativeDeadline = adjustForWeekend(tentativeDeadline);

    const result = { start: earliestStart, deadline: tentativeDeadline };
    dueDates[taskId] = result;
    return result;
  }

  // Berechne für alle Tasks die Daten
  tasks.forEach(task => {
    computeDueDates(task.id);
  });

  // Hänge die formatierten Daten an die Tasks an
  const resultTasks = tasks.map(task => {
    const dates = dueDates[task.id];
    return {
      ...task,
      taskDatum: formatDate(dates.start),
      deadline: formatDate(dates.deadline)
    };
  });

  return {
    Tasks: resultTasks,
    Connections: connections
  };
}